/**
 * LandingPage.jsx — Premium tenant public landing page (revamp).
 *
 * Composes the 17 themed sections under TenantThemeProvider + TenantLayout.
 * Legacy version preserved at /:garageSlug/legacy via LandingPageLegacy.jsx.
 */

import React, { useEffect, useState, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ChevronRight, Plus, BadgeCheck, Droplets, CircleDot, Disc, Link as LinkIcon, BatteryCharging, Lightbulb, Cog, Wrench, Package } from 'lucide-react';

import { TenantThemeProvider, useTenantTheme } from '../components/tenant/TenantThemeContext';
import TenantLayout from '../components/tenant/TenantLayout';
import TenantSEO from '../components/tenant/TenantSEO';
import TenantLoader from '../components/tenant/ui/TenantLoader';
import Hero from '../components/tenant/sections/Hero';
import { RevealOnScroll, MagneticButton, SectionHeader } from '../components/tenant/ui/primitives';
import { useLanguage } from '../contexts/LanguageContext';
import { formatCurrency } from '../lib/i18n';
import { motion } from 'framer-motion';

// ---------------------------------------------------------------------------
// Code-split below-the-fold section groups. Hero stays eager (above the fold);
// Discovery / Action / Showcase / Closing become four async chunks. Webpack
// dedupes shared imports across these chunks so this is a pure perf win.
// `lazyNamed` adapts a named export → React.lazy default-export contract.
// ---------------------------------------------------------------------------
const lazyNamed = (loader, name) =>
    React.lazy(() => loader().then(m => ({ default: m[name] })));

const DiscoveryMod = () => import(/* webpackChunkName: "tenant-discovery" */ '../components/tenant/sections/Discovery');
const ActionMod = () => import(/* webpackChunkName: "tenant-action" */ '../components/tenant/sections/Action');
const ShowcaseMod = () => import(/* webpackChunkName: "tenant-showcase" */ '../components/tenant/sections/Showcase');
const ClosingMod = () => import(/* webpackChunkName: "tenant-closing" */ '../components/tenant/sections/Closing');

const TrustStrip = lazyNamed(DiscoveryMod, 'TrustStrip');
const WhyChooseUs = lazyNamed(DiscoveryMod, 'WhyChooseUs');
const Specializations = lazyNamed(DiscoveryMod, 'Specializations');
const Services = lazyNamed(DiscoveryMod, 'Services');

const Process = lazyNamed(ActionMod, 'Process');
const JobTracker = lazyNamed(ActionMod, 'JobTracker');
const Booking = lazyNamed(ActionMod, 'Booking');

const BeforeAfter = lazyNamed(ShowcaseMod, 'BeforeAfter');
const RealWorkReel = lazyNamed(ShowcaseMod, 'RealWorkReel');
const Reviews = lazyNamed(ShowcaseMod, 'Reviews');
const Team = lazyNamed(ShowcaseMod, 'Team');
const BlogTeaser = lazyNamed(ShowcaseMod, 'BlogTeaser');

const Convenience = lazyNamed(ClosingMod, 'Convenience');
const MapSection = lazyNamed(ClosingMod, 'MapSection');
const FAQ = lazyNamed(ClosingMod, 'FAQ');
const FinalCTA = lazyNamed(ClosingMod, 'FinalCTA');

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
    const { garageSlug } = useParams();
    const [tenant, setTenant] = useState(null);
    const [services, setServices] = useState([]);
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!garageSlug) return;
        let cancelled = false;
        setLoading(true);
        setNotFound(false);

        const siteReq = axios.get(`${API}/tenants/${garageSlug}/site`)
            .catch(() => axios.get(`${API}/tenants/${garageSlug}/public`));
        const servicesReq = axios.get(`${API}/tenants/${garageSlug}/services`).catch(() => ({ data: [] }));
        const partsReq = axios.get(`${API}/tenants/${garageSlug}/parts`).catch(() => ({ data: [] }));

        Promise.all([siteReq, servicesReq, partsReq])
            .then(([siteRes, servicesRes, partsRes]) => {
                if (cancelled) return;
                setTenant(siteRes.data);
                setServices(Array.isArray(servicesRes.data) ? servicesRes.data : []);
                setParts(Array.isArray(partsRes.data) ? partsRes.data : []);
            })
            .catch(err => {
                if (cancelled) return;
                if (err?.response?.status === 404) setNotFound(true);
                console.error('Tenant fetch failed', err);
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [garageSlug]);

    if (loading) return <TenantLoader slug={garageSlug} />;
    if (notFound || !tenant) return <NotFoundScreen slug={garageSlug} />;

    const slugBase = `/${garageSlug}`;

    return (
        <TenantThemeProvider tenant={tenant}>
            <TenantSEO tenant={tenant} slug={garageSlug} page="home" services={services} />
            <TenantLayout>
                <LandingComposer
                    slugBase={slugBase}
                    garageSlug={garageSlug}
                    services={services}
                    parts={parts}
                />
            </TenantLayout>
        </TenantThemeProvider>
    );
}

// ============================================================
// Per-template section ordering. Same components, different
// narrative arc. Sections still gated by `sectionEnabled` inside
// each component. Source of truth: components/tenant/templates.js
// ============================================================
import { SECTION_ORDERS as SECTION_ORDER_MAP } from '../components/tenant/templates';
const SECTION_ORDERS = SECTION_ORDER_MAP;

function LandingComposer({ slugBase, garageSlug, services, parts }) {
    const { templateId } = useTenantTheme();
    const order = SECTION_ORDERS[templateId] || SECTION_ORDERS.forest;
    const map = {
        hero: <Hero key="hero" slugBase={slugBase} />,
        trust: <TrustStrip key="trust" />,
        why: <WhyChooseUs key="why" />,
        specializations: <Specializations key="specializations" />,
        services: <Services key="services" services={services} slugBase={slugBase} />,
        parts: <PartsPreview key="parts" parts={parts} slugBase={slugBase} />,
        process: <Process key="process" />,
        jobtracker: <JobTracker key="jobtracker" slug={garageSlug} />,
        beforeAfter: <BeforeAfter key="beforeAfter" />,
        convenience: <Convenience key="convenience" />,
        realWork: <RealWorkReel key="realWork" />,
        reviews: <Reviews key="reviews" slug={garageSlug} />,
        team: <Team key="team" />,
        booking: <Booking key="booking" slug={garageSlug} services={services} />,
        blog: <BlogTeaser key="blog" slug={garageSlug} slugBase={slugBase} />,
        map: <MapSection key="map" />,
        faq: <FAQ key="faq" />,
        finalcta: <FinalCTA key="finalcta" />
    };
    return (
        <Suspense fallback={<SectionSkeleton />}>
            {order.map(k => map[k]).filter(Boolean)}
        </Suspense>
    );
}

// Lightweight skeleton used while async section chunks load. Roughly the
// height of a typical section so layout is stable and CLS stays at 0.
function SectionSkeleton() {
    return (
        <div
            aria-hidden
            className="relative w-full py-16 md:py-20"
            style={{ minHeight: '320px' }}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <div className="h-6 w-40 rounded-md bg-[var(--tenant-surface-alt)] animate-pulse" />
                <div className="mt-4 h-10 w-2/3 rounded-md bg-[var(--tenant-surface-alt)] animate-pulse" />
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[0, 1, 2].map(i => (
                        <div key={i} className="h-40 rounded-2xl bg-[var(--tenant-surface-alt)] animate-pulse" />
                    ))}
                </div>
            </div>
        </div>
    );
}

function NotFoundScreen({ slug }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f8f6f0] text-[#0c1a12] px-6">
            <div className="max-w-md text-center">
                <AlertTriangle className="w-12 h-12 mx-auto text-amber-600 mb-4" />
                <h1 className="font-display text-4xl tracking-wider">Workshop not found</h1>
                <p className="mt-3 text-[#0c1a12]/60 text-sm">
                    We couldn't find a garage at <span className="font-mono">/{slug}</span>.
                </p>
                <Link to="/" className="inline-block mt-6 px-5 py-2 rounded-full bg-[#0c1a12] text-white font-semibold text-sm uppercase tracking-wide hover:brightness-110">
                    Back to Motopotti
                </Link>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// PartsPreview — slim 3-card teaser linking to the full store
// ---------------------------------------------------------------------------
const PARTS_CATEGORY_LABELS = {
    engine_oil: { en: 'Engine Oil', bn: 'ইঞ্জিন অয়েল' },
    tires: { en: 'Tires', bn: 'টায়ার' },
    brakes: { en: 'Brakes', bn: 'ব্রেক' },
    chain: { en: 'Chain & Sprocket', bn: 'চেইন' },
    battery: { en: 'Battery', bn: 'ব্যাটারি' },
    lights: { en: 'Lights', bn: 'লাইট' },
    accessories: { en: 'Accessories', bn: 'অ্যাকসেসরিজ' }
};

function pickPartIcon(raw) {
    const key = String(raw || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (/oil|lubric|grease|coolant|fluid/.test(key)) return Droplets;
    if (/tyre|tire|wheel|rim/.test(key)) return CircleDot;
    if (/brake|pad|disc|rotor/.test(key)) return Disc;
    if (/chain|sprocket|belt/.test(key)) return LinkIcon;
    if (/batter|electric|spark|plug/.test(key)) return BatteryCharging;
    if (/light|bulb|lamp|head|tail|indicator/.test(key)) return Lightbulb;
    if (/tool|wrench|spanner/.test(key)) return Wrench;
    if (/access|grip|mirror|cover|seat/.test(key)) return Package;
    return Cog;
}

function partPriceLP(p) {
    if (!p) return 0;
    const v = p.selling_price ?? p.price ?? p.mrp ?? 0;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
}

function PartsPreview({ parts, slugBase }) {
    const { language } = useLanguage();
    const featured = (parts || []).filter(p => p.is_active !== false).slice(0, 4);
    if (featured.length === 0) return null;

    return (
        <section id="store" className="relative py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <RevealOnScroll>
                    <div className="flex items-end justify-between gap-6 flex-wrap">
                        <SectionHeader
                            eyebrow={language === 'bn' ? 'পার্টস স্টোর' : 'Parts Store'}
                            title="Genuine parts, one roof"
                            titleBn="জেনুইন পার্টস, এক ছাদের নিচে"
                        />
                        <MagneticButton as={Link} to={`${slugBase}/store`} variant="secondary" size="md" icon={<ChevronRight className="w-4 h-4" />}>
                            {language === 'bn' ? 'সব পার্টস দেখুন' : 'View all parts'}
                        </MagneticButton>
                    </div>
                </RevealOnScroll>

                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {featured.map((p, i) => {
                        const Icon = pickPartIcon(p.category);
                        const name = (language === 'bn' && p.name_bn) ? p.name_bn : p.name;
                        const catLabel = PARTS_CATEGORY_LABELS[p.category]?.[language === 'bn' ? 'bn' : 'en'] || (p.category || '').toString().replace(/[_-]/g, ' ');
                        return (
                            <RevealOnScroll key={p.id} delay={Math.min(i * 0.05, 0.2)}>
                                <Link to={`${slugBase}/store`} className="block">
                                    <motion.div
                                        whileHover={{ y: -3 }}
                                        transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                                        className="group relative h-full rounded-xl bg-[var(--tenant-surface)] border border-[var(--tenant-border)] hover:border-[var(--tenant-primary)]/40 hover:shadow-[0_18px_40px_-26px_var(--tenant-primary)] transition-all duration-300 overflow-hidden flex"
                                    >
                                        <div className="w-1 bg-[var(--tenant-primary)] group-hover:w-1.5 transition-all duration-300" />
                                        <div className="flex-1 p-4 flex flex-col">
                                            <div className="flex items-start justify-between gap-2">
                                                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] group-hover:bg-[var(--tenant-primary)] group-hover:text-[var(--tenant-surface)] transition-colors">
                                                    <Icon className="w-4 h-4" />
                                                </span>
                                                {p.is_genuine && (
                                                    <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] border border-[var(--tenant-primary)]/25 text-[9px] uppercase tracking-[0.18em] font-semibold">
                                                        <BadgeCheck className="w-3 h-3" /> Genuine
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-3 text-[9px] uppercase tracking-[0.24em] text-[var(--tenant-muted)] truncate">
                                                {p.brand && <span className="font-semibold text-[var(--tenant-ink)]/70">{p.brand} · </span>}
                                                {catLabel}
                                            </div>
                                            <h3 className={['mt-2 font-headline font-medium text-lg md:text-xl tracking-tight text-[var(--tenant-ink)] leading-snug line-clamp-2 min-h-[2.6em]', language === 'bn' && p.name_bn ? 'font-bn' : ''].join(' ')}>
                                                {name}
                                            </h3>
                                            <div className="mt-auto pt-4 flex items-end justify-between gap-2">
                                                {partPriceLP(p) > 0 ? (
                                                    <div>
                                                        <div className="text-[9px] uppercase tracking-[0.28em] text-[var(--tenant-muted)]">Price</div>
                                                        <div className="font-headline font-semibold text-xl text-[var(--tenant-ink)] tracking-tight leading-none mt-0.5">
                                                            {formatCurrency(partPriceLP(p), language)}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-[var(--tenant-muted)]">
                                                        {language === 'bn' ? 'দাম জানতে ইনকোয়ারি' : 'Ask for price'}
                                                    </div>
                                                )}
                                                <span className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] text-[11px] uppercase tracking-[0.16em] font-semibold group-hover:bg-[var(--tenant-primary)] group-hover:text-[var(--tenant-surface)] transition">
                                                    <Plus className="w-3 h-3" />
                                                    {language === 'bn' ? 'যোগ' : 'Add'}
                                                </span>
                                            </div>
                                        </div>
                                    </motion.div>
                                </Link>
                            </RevealOnScroll>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
